import FilterListIcon from "@mui/icons-material/FilterList";
import { InputAdornment, TextField } from "@mui/material";
import { ChangeEvent, PropsWithChildren, useEffect, useRef, useState, type JSX } from 'react';
import useDebounce from "../../../hooks/use-debounce/UseDebounce";
import { SideSheetFieldPropsBase } from "../side-sheet/SideSheet";

export type SideSheetFilterInputProps = {
    value: string;
    debounceDelay?: number;
    autoFocus?: boolean;
    onChange?: (value: string) => void;
} & SideSheetFieldPropsBase;

export default function SideSheetFilterInput({
    id,
    testId,
    label,
    ariaLabel,
    readOnly,
    helperText,
    sx,
    disabled,
    value,
    debounceDelay = 500,
    autoFocus,
    onChange
}: PropsWithChildren<SideSheetFilterInputProps>): JSX.Element {
    const [filter, setFilter] = useState(value ?? "");
    const debouncedFilter = useDebounce(filter, debounceDelay);
    const filterChangeRef = useRef(onChange);

    useEffect(() => {
        filterChangeRef.current = onChange;
    }, [onChange]);

    // Using onFilterValueChange directly requires it to be added
    // to the dependency array, which causes recursive renders.
    useEffect(() => {
        filterChangeRef.current && filterChangeRef.current(debouncedFilter);
    }, [debouncedFilter]);

    useEffect(() => {
        setFilter(value ?? "");
    }, [value]);

    return (
        <TextField
            variant="filled"
            id={id}
            data-testid={testId}
            aria-label={ariaLabel}
            label={label}
            helperText={helperText}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setFilter(event.target.value);
            }}
            value={filter}
            sx={{
                marginTop: 0,
                ...sx
            }}
            disabled={disabled}
            inputRef={ref => {
                if (autoFocus && ref) {
                    setTimeout(() => {
                        ref?.focus();
                    }, 0);
                }
            }}
            slotProps={{
                input: {
                    readOnly: readOnly,
                    startAdornment: (
                        <InputAdornment position="start">
                            <FilterListIcon />
                        </InputAdornment>
                    )
                },

                inputLabel: {
                    shrink: true,
                }
            }} />
    );
}